import React from "react";
import styled from "styled-components";

/* =======================
   Fullscreen Page Wrapper
   ======================= */
const PageWrapper = styled.div`
  width: 100vw;
  min-height: 100vh;
  background-color: #003049;
  display: flex;
  justify-content: center;
`;

/* =======================
   Content Container
   ======================= */
const Container = styled.div`
  width: 100%;
  max-width: 80rem;
  padding: 3rem 1.5rem;
  background-color: #e1f5fe;
  border-radius: 1.4rem;
  margin: 2em 4em;
`;

/* =======================
   Typography
   ======================= */
const Title = styled.h1`
  font-size: 2.25rem;
  font-weight: 700;
  text-align: center;
  color: #111827;
  margin-bottom: 1rem;
`;

const Subtitle = styled.h2`
  font-size: 1.5rem;
  font-weight: 600;
  text-align: center;
  color: #1f2937;
  margin-bottom: 2rem;
`;

const Meta = styled.div`
  text-align: center;
  font-size: 0.875rem;
  color: #4b5563;
  margin-bottom: 3rem;
`;

const Paragraph = styled.p`
  color: #374151;
  line-height: 1.7;
  margin-bottom: ${({ mb }) => mb || "1.5rem"};
  font-weight: ${({ bold }) => (bold ? "500" : "400")};
`;

/* =======================
   Sections
   ======================= */
const Section = styled.section`
  margin-bottom: 3rem;
`;

const SectionTitle = styled.h2`
  font-size: 1.5rem;
  font-weight: 700;
  color: #111827;
  margin-bottom: 1.5rem;
`;

/* =======================
   Lists
   ======================= */
const Ul = styled.ul`
  list-style: disc;
  padding-left: 1.5rem;
  color: #374151;

  li {
    margin-bottom: 0.75rem;
  }
`;

const Ol = styled.ol`
  list-style: decimal;
  padding-left: 1.5rem;
  color: #374151;

  li {
    margin-bottom: 1rem;
  }
`;

/* =======================
   Component
   ======================= */
function Ptnc() {
  return (
    <PageWrapper>
      <Container>
        <Title>NEXUS PLATFORM</Title>
        <Subtitle>TERMS AND CONDITIONS OF USE</Subtitle>

        <Meta>
          <p>
            <strong>Effective Date:</strong> [●]
          </p>
          <p>
            <strong>Last Updated:</strong> [●]
          </p>
        </Meta>

        <Paragraph>
          These Terms and Conditions (“Terms”) govern access to and use of the
          Nexus digital platform, including its website, mobile application,
          APIs, E-Wallet functionality, and related services (collectively, the
          “Platform”).
        </Paragraph>

        <Paragraph bold mb="3rem">
          By accessing or using the Platform, you confirm that you have read,
          understood, and agreed to be legally bound by these Terms.
        </Paragraph>

        {/* 1 */}
        <Section>
          <SectionTitle>1. DEFINITIONS AND INTERPRETATION</SectionTitle>
          <Paragraph>
            In these Terms, unless the context otherwise requires:
          </Paragraph>
          <Ul>
            <li>“Company” means Nexus E-Commerce Limited.</li>
            <li>“Platform” means the Nexus digital insurance platform.</li>
            <li>“Authority” means the Insurance Regulatory Authority of Kenya.</li>
            <li>“User” means a fully registered person.</li>
            <li>“Subscriber” means a partially registered person.</li>
            <li>“Customer” means a User purchasing insurance.</li>
            <li>“Insurer” means a licensed insurance company.</li>
            <li>“Intermediary” means a licensed agent or broker.</li>
            <li>“E-Wallet” means the digital wallet facility.</li>
            <li>“Transaction” means any interaction on the Platform.</li>
          </Ul>
        </Section>

        {/* 2 */}
        <Section>
          <SectionTitle>2. ELIGIBILITY AND AUTHORITY</SectionTitle>
          <Ol>
            <li>You must be at least 18 years old.</li>
            <li>You warrant authority to bind entities.</li>
            <li>All information must be accurate and lawful.</li>
          </Ol>
        </Section>

        {/* 3 */}
        <Section>
          <SectionTitle>3. NATURE OF THE PLATFORM</SectionTitle>
          <Ol>
            <li>The Platform facilitates insurer–customer interactions.</li>
            <li>Nexus is not an insurer, broker, or bank.</li>
            <li>Insurance contracts exist solely with Insurers.</li>
          </Ol>
        </Section>

        {/* 4 */}
        <Section>
          <SectionTitle>4. PLATFORM NEUTRALITY</SectionTitle>
          <Ol>
            <li>No product recommendation or guarantee.</li>
            <li>All underwriting decisions are insurer-led.</li>
            <li>No insurance business is carried on by Nexus.</li>
          </Ol>
        </Section>

        {/* 5 */}
        <Section>
          <SectionTitle>5. ACCOUNT REGISTRATION AND SECURITY</SectionTitle>
          <Ol>
            <li>Registration may be required.</li>
            <li>You are responsible for credential security.</li>
            <li>Accounts are non-transferable.</li>
            <li>Nexus may suspend or terminate access.</li>
          </Ol>
        </Section>

        {/* 6 */}
        <Section>
          <SectionTitle>6. ELECTRONIC COMMUNICATIONS</SectionTitle>
          <Ol>
            <li>You consent to electronic notices.</li>
            <li>Electronic notices meet legal requirements.</li>
          </Ol>
        </Section>

        {/* 7 */}
        <Section>
          <SectionTitle>7. INTELLECTUAL PROPERTY</SectionTitle>
          <Ol>
            <li>All IP belongs to Nexus or licensors.</li>
            <li>Limited licence granted for lawful use.</li>
            <li>No copying or reverse engineering.</li>
          </Ol>
        </Section>

        {/* 8 */}
        <Section>
          <SectionTitle>8. USER CONDUCT</SectionTitle>
          <Ul>
            <li>No false or misleading information.</li>
            <li>No scraping or unauthorised marketing.</li>
            <li>No infringement of rights.</li>
          </Ul>
        </Section>

        {/* 9 */}
        <Section>
          <SectionTitle>9. INSURANCE SELECTION</SectionTitle>
          <Ol>
            <li>Users choose Insurers and Intermediaries.</li>
            <li>No automatic assignment.</li>
            <li>Data shared strictly for underwriting.</li>
          </Ol>
        </Section>

        {/* 10 */}
        <Section>
          <SectionTitle>10. E-WALLET SERVICES</SectionTitle>
          <Ol>
            <li>Used for approved transactions only.</li>
            <li>Supports KES and USD only.</li>
            <li>Funds are not bank deposits.</li>
            <li>No interest is paid.</li>
          </Ol>
        </Section>

        {/* 11 */}
        <Section>
          <SectionTitle>11. CLAIMS SUBMISSION</SectionTitle>
          <Paragraph>
            Claims may be submitted electronically. Claims assessment and
            settlement are the sole responsibility of the Insurer.
          </Paragraph>
        </Section>

        {/* 12 */}
        <Section>
          <SectionTitle>12. REVIEWS AND USER CONTENT</SectionTitle>
          <Paragraph>
            Reviews must be truthful and non-defamatory. Nexus may remove content
            at its discretion.
          </Paragraph>
        </Section>

        {/* 13 */}
        <Section>
          <SectionTitle>13. DATA PROTECTION AND PRIVACY</SectionTitle>
          <Paragraph>
            Personal data is processed in accordance with the Data Protection
            Act, 2019 (Kenya).
          </Paragraph>
        </Section>

        {/* 14 */}
        <Section>
          <SectionTitle>14. AML & CTF</SectionTitle>
          <Paragraph>
            AML/CTF obligations relating to underwriting and claims rest with
            licensed insurers.
          </Paragraph>
        </Section>

        {/* 15 */}
        <Section>
          <SectionTitle>15. DISCLAIMER OF WARRANTIES</SectionTitle>
          <Paragraph>
            The Platform is provided “as is” without warranties.
          </Paragraph>
        </Section>

        {/* 16 */}
        <Section>
          <SectionTitle>16. LIMITATION OF LIABILITY</SectionTitle>
          <Paragraph>
            Nexus shall not be liable for indirect or consequential losses.
          </Paragraph>
        </Section>

        {/* 17 */}
        <Section>
          <SectionTitle>17. INDEMNITY</SectionTitle>
          <Paragraph>
            You agree to indemnify Nexus against losses arising from misuse.
          </Paragraph>
        </Section>

        {/* 18 */}
        <Section>
          <SectionTitle>18. SUSPENSION AND TERMINATION</SectionTitle>
          <Paragraph>
            Nexus may suspend or terminate access for breach or inactivity.
          </Paragraph>
        </Section>

        {/* 19 */}
        <Section>
          <SectionTitle>19. FORCE MAJEURE</SectionTitle>
          <Paragraph>
            Neither party shall be liable for events beyond control.
          </Paragraph>
        </Section>

        {/* 20 */}
        <Section>
          <SectionTitle>20. ASSIGNMENT</SectionTitle>
          <Paragraph>
            Nexus may assign rights without notice. Users may not assign without
            consent.
          </Paragraph>
        </Section>

        {/* 21 */}
        <Section>
          <SectionTitle>21. VARIATION</SectionTitle>
          <Paragraph>
            Terms may be amended from time to time.
          </Paragraph>
        </Section>

        {/* 22 */}
        <Section>
          <SectionTitle>22. DISPUTE RESOLUTION</SectionTitle>
          <Paragraph>
            Disputes shall be resolved by arbitration seated in Nairobi.
          </Paragraph>
        </Section>

        {/* 23 */}
        <Section>
          <SectionTitle>23. GOVERNING LAW</SectionTitle>
          <Paragraph>
            These Terms are governed by the laws of Kenya.
          </Paragraph>
        </Section>

        {/* 24 */}
        <Section>
          <SectionTitle>24. ENTIRE AGREEMENT</SectionTitle>
          <Paragraph>
            These Terms constitute the entire agreement between the parties.
          </Paragraph>
        </Section>
      </Container>
    </PageWrapper>
  );
}

export default Ptnc;
